import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Pencil, Trash2, Clock, Car, FileCheck } from 'lucide-react';
import { formatCurrency, formatNumber, getCurrentDateDutch } from '@/lib/utils';
import { toast } from 'sonner';

export default function RegistratiesPage() {
  const { api, user } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('uren');
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [mileageEntries, setMileageEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Time Entry Dialog
  const [showTimeDialog, setShowTimeDialog] = useState(false);
  const [editingTime, setEditingTime] = useState(null);
  const [dialogTab, setDialogTab] = useState('uren'); // For combined dialog
  const [timeForm, setTimeForm] = useState({
    client_id: '',
    project_id: '',
    datum: getCurrentDateDutch(),
    start_tijd: '',
    eind_tijd: '',
    uren: '',
    beschrijving: '',
    uurtarief: '',
    factureerbaar: true,
    // Mileage fields for combined registration
    add_mileage: false,
    van_locatie: '',
    naar_locatie: '',
    kilometers: '',
    km_tarief: ''
  });

  // Mileage Entry Dialog
  const [showMileageDialog, setShowMileageDialog] = useState(false);
  const [editingMileage, setEditingMileage] = useState(null);
  const [mileageForm, setMileageForm] = useState({
    client_id: '',
    project_id: '',
    datum: getCurrentDateDutch(),
    van_locatie: '',
    naar_locatie: '',
    kilometers: '',
    km_tarief: '',
    beschrijving: ''
  });

  // Add Client Dialog
  const [showAddClientDialog, setShowAddClientDialog] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [addClientContext, setAddClientContext] = useState('time'); // 'time' or 'mileage'

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, projectsRes, timeRes, mileageRes] = await Promise.all([
        api.get('/clients'),
        api.get('/projects'),
        api.get('/time-entries'),
        api.get('/mileage-entries')
      ]);
      setClients(clientsRes.data);
      setProjects(projectsRes.data);
      setTimeEntries(timeRes.data);
      setMileageEntries(mileageRes.data);
    } catch (err) {
      toast.error(t('errors.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  // Add Client Handler
  const openAddClientDialog = (context) => {
    setAddClientContext(context);
    setNewClientName('');
    setShowAddClientDialog(true);
  };

  const handleAddClient = async () => {
    if (!newClientName.trim()) {
      toast.error(t('registrations.enterClientName'));
      return;
    }
    try {
      const res = await api.post('/clients', { naam: newClientName.trim() });
      setClients(prev => [...prev, res.data]);
      
      // Set the new client in the appropriate form
      if (addClientContext === 'time') {
        setTimeForm(prev => ({ ...prev, client_id: res.data.client_id }));
      } else {
        setMileageForm(prev => ({ ...prev, client_id: res.data.client_id }));
      }
      
      setNewClientName('');
      setShowAddClientDialog(false);
      toast.success(t('clients.clientSaved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.somethingWentWrong'));
    }
  };

  // Time Entry handlers
  const openTimeDialog = (entry = null) => {
    if (entry) {
      setEditingTime(entry);
      setTimeForm({
        client_id: entry.client_id,
        project_id: entry.project_id || '',
        datum: entry.datum,
        start_tijd: entry.start_tijd || '',
        eind_tijd: entry.eind_tijd || '',
        uren: entry.uren?.toString() || '',
        beschrijving: entry.beschrijving || '',
        uurtarief: entry.uurtarief?.toString() || '',
        factureerbaar: entry.factureerbaar,
        add_mileage: false,
        van_locatie: '',
        naar_locatie: '',
        kilometers: '',
        km_tarief: user?.standaard_km_tarief?.toString() || '0.23'
      });
      setDialogTab('uren');
    } else {
      setEditingTime(null);
      setTimeForm({
        client_id: '',
        project_id: '',
        datum: getCurrentDateDutch(),
        start_tijd: '',
        eind_tijd: '',
        uren: '',
        beschrijving: '',
        uurtarief: user?.standaard_uurtarief?.toString() || '75',
        factureerbaar: true,
        add_mileage: false,
        van_locatie: '',
        naar_locatie: '',
        kilometers: '',
        km_tarief: user?.standaard_km_tarief?.toString() || '0.23'
      });
      setDialogTab('uren');
    }
    setShowTimeDialog(true);
  };

  const saveTimeEntry = async () => {
    if (!timeForm.client_id) {
      toast.error(t('registrations.selectClient'));
      return;
    }
    if (!timeForm.uren && (!timeForm.start_tijd || !timeForm.eind_tijd)) {
      toast.error(t('registrations.fillHoursOrTime'));
      return;
    }

    try {
      const data = {
        client_id: timeForm.client_id,
        project_id: timeForm.project_id || null,
        datum: timeForm.datum,
        start_tijd: timeForm.start_tijd || null,
        eind_tijd: timeForm.eind_tijd || null,
        uren: timeForm.uren ? parseFloat(timeForm.uren) : null,
        beschrijving: timeForm.beschrijving,
        uurtarief: timeForm.uurtarief ? parseFloat(timeForm.uurtarief) : null,
        factureerbaar: timeForm.factureerbaar
      };

      if (editingTime) {
        await api.put(`/time-entries/${editingTime.entry_id}`, data);
        toast.success(t('registrations.entrySaved'));
      } else {
        await api.post('/time-entries', data);
        toast.success(t('registrations.timeEntrySaved'));
        
        // Also save mileage if tab is active and has data
        if (dialogTab === 'uren-km' && timeForm.kilometers) {
          await api.post('/mileage-entries', {
            client_id: timeForm.client_id,
            project_id: timeForm.project_id || null,
            datum: timeForm.datum,
            van_locatie: timeForm.van_locatie || null,
            naar_locatie: timeForm.naar_locatie || null,
            kilometers: parseFloat(timeForm.kilometers),
            km_tarief: timeForm.km_tarief ? parseFloat(timeForm.km_tarief) : null,
            beschrijving: timeForm.beschrijving
          });
          toast.success(t('registrations.mileageAlsoSaved'));
        }
      }
      
      setShowTimeDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.somethingWentWrong'));
    }
  };

  const deleteTimeEntry = async (entry) => {
    if (!confirm(t('registrations.confirmDelete'))) return;
    
    try {
      await api.delete(`/time-entries/${entry.entry_id}`);
      toast.success(t('registrations.entryDeleted'));
      fetchData();
    } catch (err) {
      toast.error(t('errors.somethingWentWrong'));
    }
  };

  // Mileage Entry handlers
  const openMileageDialog = (entry = null) => {
    if (entry) {
      setEditingMileage(entry);
      setMileageForm({
        client_id: entry.client_id,
        project_id: entry.project_id || '',
        datum: entry.datum,
        van_locatie: entry.van_locatie || '',
        naar_locatie: entry.naar_locatie || '',
        kilometers: entry.kilometers?.toString() || '',
        km_tarief: entry.km_tarief?.toString() || '',
        beschrijving: entry.beschrijving || ''
      });
    } else {
      setEditingMileage(null);
      setMileageForm({
        client_id: '',
        project_id: '',
        datum: getCurrentDateDutch(),
        van_locatie: '',
        naar_locatie: '',
        kilometers: '',
        km_tarief: user?.standaard_km_tarief?.toString() || '0.23',
        beschrijving: ''
      });
    }
    setShowMileageDialog(true);
  };

  const saveMileageEntry = async () => {
    if (!mileageForm.client_id) {
      toast.error(t('registrations.selectClient'));
      return;
    }
    if (!mileageForm.kilometers) {
      toast.error(t('registrations.fillKilometers'));
      return;
    }

    try {
      const data = {
        client_id: mileageForm.client_id,
        project_id: mileageForm.project_id || null,
        datum: mileageForm.datum,
        van_locatie: mileageForm.van_locatie || null,
        naar_locatie: mileageForm.naar_locatie || null,
        kilometers: parseFloat(mileageForm.kilometers),
        km_tarief: mileageForm.km_tarief ? parseFloat(mileageForm.km_tarief) : null,
        beschrijving: mileageForm.beschrijving
      };

      if (editingMileage) {
        await api.put(`/mileage-entries/${editingMileage.entry_id}`, data);
        toast.success(t('registrations.tripUpdated'));
      } else {
        await api.post('/mileage-entries', data);
        toast.success(t('registrations.tripSaved'));
      }
      
      setShowMileageDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.somethingWentWrong'));
    }
  };

  const deleteMileageEntry = async (entry) => {
    if (!confirm(t('registrations.confirmDeleteTrip'))) return;
    
    try {
      await api.delete(`/mileage-entries/${entry.entry_id}`);
      toast.success(t('registrations.tripDeleted'));
      fetchData();
    } catch (err) {
      toast.error(t('errors.somethingWentWrong'));
    }
  };

  if (loading) {
    return <div className="animate-pulse space-y-4">
      <div className="h-12 bg-muted rounded-xl w-1/3" />
      <div className="h-64 bg-muted rounded-xl" />
    </div>;
  }

  return (
    <div className="space-y-6" data-testid="registraties-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-heading text-foreground">{t('registrations.title')}</h1>
          <p className="text-muted-foreground">{t('registrations.subtitle')}</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <TabsList className="grid w-full sm:w-auto grid-cols-2">
            <TabsTrigger value="uren" className="flex items-center gap-2" data-testid="tab-uren">
              <Clock className="w-4 h-4" />
              {t('common.hours')}
            </TabsTrigger>
            <TabsTrigger value="ritten" className="flex items-center gap-2" data-testid="tab-ritten">
              <Car className="w-4 h-4" />
              {t('registrations.trips')}
            </TabsTrigger>
          </TabsList>
          
          <Button 
            className="bg-cyan-500 hover:bg-cyan-600 btn-active"
            onClick={() => activeTab === 'uren' ? openTimeDialog() : openMileageDialog()}
            data-testid="add-entry-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {activeTab === 'uren' ? t('registrations.addTimeEntry') : t('registrations.addMileageEntry')}
          </Button>
        </div>

        <TabsContent value="uren" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {timeEntries.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('common.date')}</TableHead>
                        <TableHead>{t('common.client')}</TableHead>
                        <TableHead>{t('common.description')}</TableHead>
                        <TableHead className="text-right">{t('common.hours')}</TableHead>
                        <TableHead className="text-right">{t('registrations.hourlyRate')}</TableHead>
                        <TableHead className="text-right">{t('common.total')}</TableHead>
                        <TableHead className="text-center">{t('common.status')}</TableHead>
                        <TableHead className="w-24"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {timeEntries.map((entry) => (
                        <TableRow key={entry.entry_id}>
                          <TableCell className="font-medium">{entry.datum}</TableCell>
                          <TableCell>{entry.client_naam}</TableCell>
                          <TableCell className="max-w-xs truncate">{entry.beschrijving || '-'}</TableCell>
                          <TableCell className="text-right font-mono">{formatNumber(entry.uren, 2)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(entry.uurtarief)}</TableCell>
                          <TableCell className="text-right font-semibold">{formatCurrency(entry.totaal)}</TableCell>
                          <TableCell className="text-center">
                            {entry.gefactureerd ? (
                              <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                <FileCheck className="w-3 h-3" /> {t('registrations.invoiced')}
                              </span>
                            ) : entry.factureerbaar ? (
                              <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">{t('registrations.billable')}</span>
                            ) : (
                              <span className="text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded-full">{t('registrations.notBillable')}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => openTimeDialog(entry)}
                                disabled={entry.gefactureerd}
                                data-testid={`edit-time-${entry.entry_id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => deleteTimeEntry(entry)}
                                disabled={entry.gefactureerd}
                                className="text-destructive hover:text-destructive"
                                data-testid={`delete-time-${entry.entry_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">{t('registrations.noTimeEntries')}</p>
                  <Button onClick={() => openTimeDialog()} className="bg-cyan-500 hover:bg-cyan-600">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('registrations.firstEntry')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ritten" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {mileageEntries.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('common.date')}</TableHead>
                        <TableHead>{t('common.client')}</TableHead>
                        <TableHead>{t('registrations.route')}</TableHead>
                        <TableHead className="text-right">{t('registrations.km')}</TableHead>
                        <TableHead className="text-right">{t('registrations.rate')}</TableHead>
                        <TableHead className="text-right">{t('common.total')}</TableHead>
                        <TableHead className="text-center">{t('common.status')}</TableHead>
                        <TableHead className="w-24"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mileageEntries.map((entry) => (
                        <TableRow key={entry.entry_id}>
                          <TableCell className="font-medium">{entry.datum}</TableCell>
                          <TableCell>{entry.client_naam}</TableCell>
                          <TableCell className="max-w-xs">
                            <span className="truncate block">
                              {entry.van_locatie && entry.naar_locatie 
                                ? `${entry.van_locatie} → ${entry.naar_locatie}`
                                : entry.van_locatie 
                                  ? `${t('registrations.from')} ${entry.van_locatie}`
                                  : entry.naar_locatie 
                                    ? `${t('registrations.to')} ${entry.naar_locatie}`
                                    : '-'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-mono">{formatNumber(entry.kilometers, 0)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(entry.km_tarief)}</TableCell>
                          <TableCell className="text-right font-semibold">{formatCurrency(entry.totaal)}</TableCell>
                          <TableCell className="text-center">
                            {entry.gefactureerd ? (
                              <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                <FileCheck className="w-3 h-3" /> {t('registrations.invoiced')}
                              </span>
                            ) : (
                              <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">{t('registrations.billable')}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => openMileageDialog(entry)}
                                disabled={entry.gefactureerd}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => deleteMileageEntry(entry)}
                                disabled={entry.gefactureerd}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Car className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">{t('registrations.noMileageEntries')}</p>
                  <Button onClick={() => openMileageDialog()} className="bg-cyan-500 hover:bg-cyan-600">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('registrations.firstTrip')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Time Entry Dialog with Tabs */}
      <Dialog open={showTimeDialog} onOpenChange={setShowTimeDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingTime ? t('registrations.editTimeEntry') : t('registrations.newEntry')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('common.date')} *</Label>
                <Input
                  value={timeForm.datum}
                  onChange={(e) => setTimeForm(prev => ({ ...prev, datum: e.target.value }))}
                  placeholder="dd-mm-yyyy"
                  data-testid="time-datum-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('common.client')} *</Label>
                <div className="flex gap-2">
                  <Select value={timeForm.client_id} onValueChange={(v) => setTimeForm(prev => ({ ...prev, client_id: v, project_id: '' }))}>
                    <SelectTrigger className="flex-1" data-testid="time-client-select">
                      <SelectValue placeholder={t('registrations.select')} />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(c => (
                        <SelectItem key={c.client_id} value={c.client_id}>{c.naam}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={() => openAddClientDialog('time')}
                    data-testid="add-client-time-btn"
                    title={t('clients.addClient')}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Project selector - only show if client is selected */}
            {timeForm.client_id && projects.filter(p => p.client_id === timeForm.client_id && p.actief).length > 0 && (
              <div className="space-y-2">
                <Label>{t('common.project')} ({t('registrations.optional')})</Label>
                <Select value={timeForm.project_id} onValueChange={(v) => setTimeForm(prev => ({ ...prev, project_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger data-testid="time-project-select">
                    <SelectValue placeholder={t('registrations.noProject')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('registrations.noProject')}</SelectItem>
                    {projects.filter(p => p.client_id === timeForm.client_id && p.actief).map(p => (
                      <SelectItem key={p.project_id} value={p.project_id}>{p.naam}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Tabs for Uren only or Uren + Kilometers */}
            {!editingTime && (
              <Tabs value={dialogTab} onValueChange={setDialogTab} className="mt-2">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="uren" className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {t('registrations.hoursOnly')}
                  </TabsTrigger>
                  <TabsTrigger value="uren-km" className="flex items-center gap-2">
                    <Car className="w-4 h-4" />
                    {t('registrations.hoursAndKm')}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            )}

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('registrations.startTime')}</Label>
                <Input
                  type="time"
                  value={timeForm.start_tijd}
                  onChange={(e) => setTimeForm(prev => ({ ...prev, start_tijd: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('registrations.endTime')}</Label>
                <Input
                  type="time"
                  value={timeForm.eind_tijd}
                  onChange={(e) => setTimeForm(prev => ({ ...prev, eind_tijd: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('registrations.orHours')}</Label>
                <Input
                  type="number"
                  step="0.25"
                  value={timeForm.uren}
                  onChange={(e) => setTimeForm(prev => ({ ...prev, uren: e.target.value }))}
                  placeholder="1.5"
                  data-testid="time-uren-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('registrations.hourlyRate')} (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={timeForm.uurtarief}
                onChange={(e) => setTimeForm(prev => ({ ...prev, uurtarief: e.target.value }))}
                placeholder="75.00"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('common.description')}</Label>
              <Textarea
                value={timeForm.beschrijving}
                onChange={(e) => setTimeForm(prev => ({ ...prev, beschrijving: e.target.value }))}
                placeholder={t('registrations.workDescription')}
                data-testid="time-beschrijving-input"
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={timeForm.factureerbaar}
                onCheckedChange={(v) => setTimeForm(prev => ({ ...prev, factureerbaar: v }))}
              />
              <Label>{t('registrations.billable')}</Label>
            </div>

            {/* Kilometers section when tab is active */}
            {dialogTab === 'uren-km' && !editingTime && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200 space-y-4">
                <p className="text-sm text-green-700 font-medium flex items-center gap-2">
                  <Car className="w-4 h-4" />
                  {t('registrations.alsoAddKm')}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-sm">{t('registrations.fromLocation')} ({t('registrations.optional')})</Label>
                    <Input
                      placeholder="Amsterdam"
                      value={timeForm.van_locatie}
                      onChange={(e) => setTimeForm(prev => ({ ...prev, van_locatie: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">{t('registrations.toLocation')} ({t('registrations.optional')})</Label>
                    <Input
                      placeholder="Rotterdam"
                      value={timeForm.naar_locatie}
                      onChange={(e) => setTimeForm(prev => ({ ...prev, naar_locatie: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-sm">{t('common.kilometers')} *</Label>
                    <Input
                      type="number"
                      placeholder="50"
                      value={timeForm.kilometers}
                      onChange={(e) => setTimeForm(prev => ({ ...prev, kilometers: e.target.value }))}
                      data-testid="time-km-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">{t('registrations.kmRate')} (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.23"
                      value={timeForm.km_tarief}
                      onChange={(e) => setTimeForm(prev => ({ ...prev, km_tarief: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTimeDialog(false)}>{t('common.cancel')}</Button>
            <Button className="bg-cyan-500 hover:bg-cyan-600" onClick={saveTimeEntry} data-testid="time-save-btn">
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mileage Entry Dialog */}
      <Dialog open={showMileageDialog} onOpenChange={setShowMileageDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingMileage ? t('registrations.editMileageEntry') : t('registrations.newMileageEntry')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('common.date')} *</Label>
                <Input
                  value={mileageForm.datum}
                  onChange={(e) => setMileageForm(prev => ({ ...prev, datum: e.target.value }))}
                  placeholder="dd-mm-yyyy"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('common.client')} *</Label>
                <div className="flex gap-2">
                  <Select value={mileageForm.client_id} onValueChange={(v) => setMileageForm(prev => ({ ...prev, client_id: v, project_id: '' }))}>
                    <SelectTrigger className="flex-1" data-testid="mileage-client-select">
                      <SelectValue placeholder={t('registrations.select')} />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(c => (
                        <SelectItem key={c.client_id} value={c.client_id}>{c.naam}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={() => openAddClientDialog('mileage')}
                    data-testid="add-client-mileage-btn"
                    title={t('clients.addClient')}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Project selector for mileage */}
            {mileageForm.client_id && projects.filter(p => p.client_id === mileageForm.client_id && p.actief).length > 0 && (
              <div className="space-y-2">
                <Label>{t('common.project')} ({t('registrations.optional')})</Label>
                <Select value={mileageForm.project_id} onValueChange={(v) => setMileageForm(prev => ({ ...prev, project_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger data-testid="mileage-project-select">
                    <SelectValue placeholder={t('registrations.noProject')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('registrations.noProject')}</SelectItem>
                    {projects.filter(p => p.client_id === mileageForm.client_id && p.actief).map(p => (
                      <SelectItem key={p.project_id} value={p.project_id}>{p.naam}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('registrations.fromLocation')} ({t('registrations.optional')})</Label>
                <Input
                  value={mileageForm.van_locatie}
                  onChange={(e) => setMileageForm(prev => ({ ...prev, van_locatie: e.target.value }))}
                  placeholder="Amsterdam"
                  data-testid="mileage-van-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('registrations.toLocation')} ({t('registrations.optional')})</Label>
                <Input
                  value={mileageForm.naar_locatie}
                  onChange={(e) => setMileageForm(prev => ({ ...prev, naar_locatie: e.target.value }))}
                  placeholder="Rotterdam"
                  data-testid="mileage-naar-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('common.kilometers')} *</Label>
                <Input
                  type="number"
                  value={mileageForm.kilometers}
                  onChange={(e) => setMileageForm(prev => ({ ...prev, kilometers: e.target.value }))}
                  placeholder="50"
                  data-testid="mileage-km-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('registrations.kmRate')} (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={mileageForm.km_tarief}
                  onChange={(e) => setMileageForm(prev => ({ ...prev, km_tarief: e.target.value }))}
                  placeholder="0.23"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('common.description')}</Label>
              <Textarea
                value={mileageForm.beschrijving}
                onChange={(e) => setMileageForm(prev => ({ ...prev, beschrijving: e.target.value }))}
                placeholder={t('registrations.clientVisit')}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMileageDialog(false)}>{t('common.cancel')}</Button>
            <Button className="bg-cyan-500 hover:bg-cyan-600" onClick={saveMileageEntry} data-testid="mileage-save-btn">
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Client Dialog */}
      <Dialog open={showAddClientDialog} onOpenChange={setShowAddClientDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{t('clients.addClient')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('clients.name')} *</Label>
              <Input
                placeholder={t('registrations.companyOrName')}
                value={newClientName}
                onChange={(e) => setNewClientName(e.target.value)}
                data-testid="new-client-name-input"
                onKeyDown={(e) => e.key === 'Enter' && handleAddClient()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddClientDialog(false)}>{t('common.cancel')}</Button>
            <Button className="bg-cyan-500 hover:bg-cyan-600" onClick={handleAddClient} data-testid="save-new-client-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t('common.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
